/**
 * camposDelActa — lo que cada casilla del acta necesita para editarse bien.
 *
 * Dos de la cabecera que no se escriben como las demás, y cuánto sitio pedir
 * para las columnas de una tabla.
 *
 * Todo lo que la plantilla marca como `valor` llega aquí siendo lo mismo: una
 * caja de texto de una línea. Pero dos de esas casillas no son texto libre de
 * una línea, y tratarlas como si lo fueran cuesta trabajo cada vez que se
 * escribe un acta.
 *
 * ── Quien convoca es una persona, no una cadena ─────────────────────────
 *
 * Es la misma persona que ya está en el directorio de la empresa, con su
 * nombre escrito de una sola manera. Tecleada a mano sale «Ivan Patiño», «Iván
 * A. Patiño» y «Patiño Barragán Iván Aurelio» en tres actas seguidas, y
 * entonces buscar las reuniones que convocó alguien deja de funcionar. Con el
 * mismo selector que ya se usa para asignar tareas, el nombre se elige y sale
 * igual siempre.
 *
 * Pero se admite escribirlo a mano igualmente: hay actas convocadas por gente
 * de fuera y por áreas —«Dirección de Operaciones»— y una lista cerrada dejaría
 * sin manera de ponerlo.
 *
 * ── El objetivo es un párrafo, no un renglón ────────────────────────────
 *
 * «Revisar el avance del sistema de minutas y acordar las siguientes entregas»
 * no cabe en una caja de una línea: se escribe mirando por una mirilla que se
 * desplaza sola, y lo que ya se escribió deja de verse. Va a renglón aparte y
 * crece con lo que contiene.
 *
 * ── Por qué se reconoce por la etiqueta ─────────────────────────────────
 *
 * Como el lugar, el folio y las fechas: las zonas se deducen del .docx de cada
 * organización y no hay un identificador estable que marcar. Las listas son
 * cortas a propósito — cada palabra de más es una casilla que cambia de forma
 * en el acta de alguien sin haberlo pedido.
 */

const sinTildes = (t) => String(t || '')
  .toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

// Quien convoca, quien preside, quien levanta el acta. Todas son una persona
// con nombre y apellidos.
//
// No entra «Asistentes»: esa es la tabla, y tiene su propio tratamiento. No
// entra «Área» ni «Departamento», que no son personas. Y no entra «Dirigido
// a», que en las plantillas que hemos visto es un destinatario colectivo.
const ES_PERSONA = new RegExp(
  '(^|[\\s/(-])('
  + 'convoca|convoco|convocada|convocado|convocante'
  + '|preside|presidio|presidente|presidenta'
  + '|modera|moderador|moderadora'
  + '|facilitador|facilitadora'
  + '|secretario|secretaria'
  + '|elaboro|elabora|elaborado|elaborada'
  + '|reviso|revisado|revisada'
  + '|autorizo|autorizado|autorizada'
  + '|organizador|organizadora|organiza'
  + '|responsable'
  + ')([\\s/:)-]|$)'
);

// Lo que la gente escribe en varias líneas aunque la casilla tenga una.
const ES_TEXTO_LARGO = new RegExp(
  '(^|[\\s/(-])('
  + 'objetivo|objetivos|proposito|propositos|finalidad'
  + '|asunto|asuntos|tema|temas|motivo|motivos'
  + '|agenda|alcance|justificacion'
  + '|descripcion|resumen|antecedentes'
  + '|observacion|observaciones|comentario|comentarios'
  + '|nota|notas|conclusion|conclusiones'
  + ')([\\s/:)-]|$)'
);

/** «Convocado por», «Elaboró», «Responsable de la reunión». */
export function esEtiquetaDePersona(etiqueta) {
  const texto = sinTildes(etiqueta).trim();
  if (!texto) return false;
  return ES_PERSONA.test(texto);
}

/** «Objetivo de la reunión», «Asunto», «Orden del día». */
export function esEtiquetaDeTextoLargo(etiqueta) {
  const texto = sinTildes(etiqueta).trim();
  if (!texto) return false;
  if (/orden del dia/.test(texto)) return true;
  return ES_TEXTO_LARGO.test(texto);
}


// ── Cuánto sitio pedir para cada columna de una tabla ───────────────────
//
// Dejado al navegador, el reparto sale mal siempre y por un motivo concreto:
// cada celda lleva dentro un campo de texto al 100 % de su columna, y un campo
// de texto no tiene ancho natural — pide lo mismo para «1» que para un acuerdo
// de tres renglones. Sin nada que los distinga, las cuatro columnas de la tabla
// de acuerdos se reparten el ancho a partes iguales: «Id» acaba midiendo
// cuatrocientos píxeles para un número de una cifra, «Responsable(s)» otros
// tantos para «Iván P.», y el acuerdo —lo único que hay que leer— se lee por
// una columna estrecha, a cuatro palabras por renglón.
//
// Aquí se dice lo que se sabe de antemano: un consecutivo son dos cifras, unas
// iniciales cuatro letras, una fecha diez caracteres. Las que NO se reconocen
// se quedan sin medida a propósito, y son las que absorben todo lo que sobra:
// en la tabla de acuerdos, «Acuerdos / Tareas»; en la de asistentes, «Puesto /
// Rol». Que la columna del texto largo sea la ancha no es una preferencia de
// estilo — es que en esta pantalla se revisa un acta antes de publicarla, y lo
// que no se lee cómodamente no se revisa.
//
// El orden importa: se devuelve la primera que case. «Fecha de resolución» es
// una fecha antes que ninguna otra cosa.
// Una columna que pide el nombre de alguien: «Responsable», «Responsable(s)»,
// «Nombre», «Encargado». No entra «Puesto / Rol», que es lo que esa persona
// hace y no quién es, ni «Tema pendiente», que no es nadie.
//
// Esta lista decide dos cosas a la vez, y por eso está una sola vez: cuánto
// mide la columna y si sus celdas se eligen del directorio. Separarlas dejaría
// columnas anchas sin desplegable, o desplegables sin sitio donde salir.
const ES_COLUMNA_PERSONA =
  /(responsable|nombre|asistente|participante|persona|encargado|due[ñn]o|integrante)/i;

// El consecutivo de las filas. Entero y a secas: «Id de proyecto» no lo es, y
// «Fecha» tampoco aunque empiece por otra cosa.
const ES_CONSECUTIVO = /^\s*(no\.?|n[úu]m(?:ero)?\.?|#|id|consecutivo)\s*$/i;

/**
 * ¿Esta columna es el consecutivo de las filas, el que se pone solo?
 *
 * No basta con la cabecera, y esta es la parte que importa: una plantilla puede
 * llamar «Id» a un código de acuerdo —«ACU-01», «A.3»— y renumerarlo sería
 * borrarlo. Se exige además que lo que YA hay escrito sean números pelados o
 * nada. Ante cualquier otra cosa, la columna se queda como estaba: editable y
 * sin que nadie la toque.
 *
 * @param {string} cabecera
 * @param {Array<string>} valores - lo que hay hoy en esa columna
 */
export function esColumnaDeConsecutivo(cabecera, valores) {
  if (!ES_CONSECUTIVO.test(String(cabecera || '').trim())) return false;
  if (!Array.isArray(valores)) return true;
  return valores.every(v => {
    const texto = String(v == null ? '' : v).trim();
    return texto === '' || /^\d{1,4}$/.test(texto);
  });
}

/**
 * Las filas con su consecutivo puesto al día: 1, 2, 3…
 *
 * Devuelve el MISMO array si no hacía falta cambiar nada. No es una
 * optimización: es lo que evita que renumerar cuente como una edición y dispare
 * un guardado —y una versión archivada— cada vez que se abre un acta.
 */
export function renumerar(filas, columna) {
  if (!Array.isArray(filas) || !(columna >= 0)) return filas;

  let hubo = false;
  const nuevas = filas.map((fila, i) => {
    const debe = String(i + 1);
    if (!Array.isArray(fila) || String(fila[columna] == null ? '' : fila[columna]) === debe) {
      return fila;
    }
    hubo = true;
    const copia = fila.slice();
    copia[columna] = debe;
    return copia;
  });

  return hubo ? nuevas : filas;
}

/** La columna del consecutivo de esta tabla, o -1. */
export function columnaDeConsecutivo(columnas, filas) {
  if (!Array.isArray(columnas)) return -1;
  return columnas.findIndex(
    (cab, i) => esColumnaDeConsecutivo(cab, (filas || []).map(f => (f || [])[i]))
  );
}

/** ¿Esta columna de una tabla pide el nombre de una persona? */
export function esColumnaDePersona(cabecera) {
  const texto = String(cabecera || '').trim();
  return Boolean(texto) && ES_COLUMNA_PERSONA.test(texto);
}

const ANCHOS = [
  // Un consecutivo. Entero, para no confundir «Id» con «Identificación».
  [/^\s*(no\.?|n[úu]m(?:ero)?\.?|#|id|clave|consecutivo)\s*$/i, 56],
  [/(iniciales|siglas|abreviatura)/i, 84],
  [/(firma|r[úu]brica|v[°ºo]\.?\s*b[°ºo]\.?)/i, 120],
  // La leyenda de la puntualidad, que se reconoce por traer dos códigos entre
  // paréntesis. «Responsable(s)», con uno solo, no es esto.
  [/(asistencia|puntualidad)/i, 168],
  [/\([^()]{1,8}\)[^(]*\([^()]{1,8}\)/, 168],
  // Cabe un calendario, que es lo que lleva dentro esa celda.
  [/(fecha|d[íi]a|plazo|vencimiento|entrega|resoluci[óo]n)/i, 168],
  // Un nombre de persona. Cabe el selector del directorio, que mide 220.
  [ES_COLUMNA_PERSONA, 236]
];

/**
 * El ancho que pedir para una columna, o `null` si tiene que quedarse con lo
 * que sobre.
 *
 * Se decide por la cabecera y no por lo que haya escrito debajo: el ancho de
 * una columna no puede cambiar mientras se escribe en ella.
 */
export function anchoDeColumna(cabecera) {
  const texto = String(cabecera || '').trim();
  if (!texto) return null;
  for (const [patron, ancho] of ANCHOS) {
    if (patron.test(texto)) return ancho;
  }
  return null;
}

/**
 * ¿Esta acta tiene alguna casilla de persona?
 *
 * Devuelve un booleano y no la lista a propósito: es lo que decide si hay que
 * bajar el directorio, y `regiones` se reemplaza entera en cada guardado. Con
 * una lista —un objeto nuevo cada vez— en las dependencias del efecto, cada
 * corrección volvería a pedir cientos de fichas para acabar con la misma.
 */
export function hayCampoDePersona(regiones) {
  if (!Array.isArray(regiones)) return false;
  return regiones.some(r => r && r.tipo === 'valor' && esEtiquetaDePersona(r.etiqueta));
}

const camposDelActa = {
  esEtiquetaDePersona, esEtiquetaDeTextoLargo, hayCampoDePersona, anchoDeColumna,
  esColumnaDePersona, esColumnaDeConsecutivo, columnaDeConsecutivo, renumerar
};
export default camposDelActa;
